/**
 * UserTypeSelector.tsx
 *
 * Modal for selecting user types — mirrors mobile UserTypeSelector widget.
 * Fetches GET /custom_account/user-types/, filters out fan/admin, sorted A-Z.
 * Multi-select with Done button returning selected IDs.
 */

import React, { useState } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import { useGetUserTypes, type UserTypeItem } from '../../api/services';

interface UserTypeSelectorProps {
  initialSelectedIds: number[];
  onConfirm: (selectedIds: number[], selectedItems: UserTypeItem[]) => void;
  onClose: () => void;
}

export const UserTypeSelector: React.FC<UserTypeSelectorProps> = ({
  initialSelectedIds,
  onConfirm,
  onClose,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(initialSelectedIds));
  const { data: types = [], isLoading } = useGetUserTypes();

  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="fixed inset-0 z-110 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="w-full sm:max-w-lg bg-[#0c0c0c] border border-white/10 rounded-3xl sm:rounded-4xl overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: '80vh', animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <h3 className="text-white text-lg font-semibold">Select User Types</h3>
          <button
            onClick={onClose}
            className="p-1 text-zinc-500 hover:text-white transition-colors"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 no-scrollbar">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-7 h-7 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
            </div>
          ) : types.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-10">No user types available.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {types.map((type: UserTypeItem) => {
                const isSelected = selectedIds.has(type.id);
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggle(type.id)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border transition-all text-left ${
                      isSelected
                        ? 'bg-blue-600/10 border-blue-500/40'
                        : 'bg-white/4 border-white/5 hover:bg-white/6 hover:border-white/10'
                    }`}
                  >
                    <span
                      className={`text-sm font-medium transition-colors ${
                        isSelected ? 'text-blue-400' : 'text-white'
                      }`}
                    >
                      {type.name}
                    </span>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected ? 'border-blue-500 bg-blue-500' : 'border-zinc-600'
                      }`}
                    >
                      {isSelected && <FiCheck size={11} className="text-white" strokeWidth={3} />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/5 flex items-center justify-between gap-4">
          <span className="text-zinc-500 text-sm">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'None selected'}
          </span>
          <button
            type="button"
            onClick={() =>
              onConfirm(
                Array.from(selectedIds),
                types.filter((t) => selectedIds.has(t.id)),
              )
            }
            className="px-6 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-colors"
          >
            Done
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.97); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
